import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { makeStyles } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import VolumeUpIcon from '@material-ui/icons/VolumeUp'
import { useIntl, FormattedMessage } from 'react-intl'
import useAudioPlayer from './hooks/useAudioPlayer'
import { useIsTouchDevice } from './hooks/useIsPhone'
import { evaluateInteractive } from './hooks/useLessonScoring'
import { ScenarioNodeType } from './hooks/useLessonStateMachine'
import L2OnScreenKeyboard from './interactions/L2OnScreenKeyboard'
import { CapeeshColors } from '../../../../assets/ColorPalette'

import LinHead from '../../../../assets/images/LinHead.svg'
import RonHead from '../../../../assets/images/RonHead.svg'
import AbeHead from '../../../../assets/images/AbeHead.svg'
import BellHead from '../../../../assets/images/BellHead.svg'
import SolinaHead from '../../../../assets/images/SolinaHead.svg'
import renderFormattedText from './utils/renderFormattedText'
import { playerMessages } from './CapeeshPlayer.i18n.Messages'

const MentorEnum = {
    NoMentor: 100,
    Abe: 0,
    Lin: 1,
    Bell: 2,
    Ron: 3,
    Solina: 4,
}

const InteractiveElementType = {
    NotDefined: 0,
    MultiChoice: 1,
    FillInBlankWriting: 2,
    FillInBlankDistractors: 3,
    Text: 4,
    ListenButton: 6,
    Image: 7,
    ListenButtonWithSlider: 8,
    Definition: 10,
    Instruction: 13,
    MentorAnimation: 15,
}

const AudioPlayType = {
    DontPlay: 0,
    PlayBefore: 1,
    PlayAfter: 2,
}

const NON_INTERACTIVE_TYPES = new Set([
    InteractiveElementType.Text,
    InteractiveElementType.ListenButton,
    InteractiveElementType.ListenButtonWithSlider,
    InteractiveElementType.Image,
    InteractiveElementType.Definition,
    InteractiveElementType.MentorAnimation,
    InteractiveElementType.Instruction,
])

function getMentorAvatar(mentorId) {
    switch (mentorId) {
        case MentorEnum.Abe: return AbeHead
        case MentorEnum.Lin: return LinHead
        case MentorEnum.Bell: return BellHead
        case MentorEnum.Ron: return RonHead
        case MentorEnum.Solina: return SolinaHead
        default: return null
    }
}

/**
 * Determine left/right/middle positioning for a dialogue utterance.
 * First speaker goes left, switches side when the mentor changes.
 * NoMentor (narrator, 100) renders in the middle.
 */
function getDialoguePosition(testOptions, index) {
    let currentDirection = 1
    let result = 'left'
    let searching = true

    if (index === 0) {
        return testOptions[0].mentor === MentorEnum.NoMentor ? 'middle' : 'left'
    }

    testOptions.forEach((to, i) => {
        if (!searching) return
        if (i > 0 && testOptions[i - 1].mentor !== to.mentor) {
            if (to.mentor !== MentorEnum.NoMentor) {
                if (testOptions[i - 1].mentor !== MentorEnum.NoMentor) {
                    currentDirection *= -1
                } else if (i > 1 && testOptions[i - 2].mentor !== to.mentor) {
                    currentDirection *= -1
                }
            }
        }
        if (i === index) {
            if (to.mentor === MentorEnum.NoMentor) {
                result = 'middle'
            } else {
                result = currentDirection === -1 ? 'right' : 'left'
            }
            searching = false
        }
    })

    return result
}

function calculateReadingDelay(text) {
    if (!text) return 500
    const wordCount = text.split(/\s+/).filter(Boolean).length
    return Math.min(Math.max(wordCount * 200, 800), 3500)
}

function parseBlanks(alternatives, text) {
    const blankMap = {}
    ;(alternatives || []).forEach((alt) => {
        const si = alt.startIndex
        if (si == null || si < 0) return
        if (!blankMap[si]) blankMap[si] = []
        blankMap[si].push(alt)
    })

    const sortedStarts = Object.keys(blankMap).map(Number).sort((a, b) => a - b)

    if (sortedStarts.length === 0) {
        if (!alternatives || alternatives.length === 0) {
            return { blanks: [], alternativesByBlank: [], textParts: [{ type: 'text', value: text || '' }] }
        }
        return {
            blanks: [{ startIndex: -1, length: 0, correctText: '' }],
            alternativesByBlank: [alternatives],
            textParts: [
                { type: 'text', value: text || '' },
                { type: 'blank', blankIndex: 0 },
            ],
        }
    }

    const blanks = sortedStarts.map((si) => {
        const group = blankMap[si]
        const correct = group.find((a) => a.correct) || group[0]
        return { startIndex: si, length: correct.text.length, correctText: correct.text }
    })

    const alternativesByBlank = sortedStarts.map((si) => {
        const alts = [...blankMap[si]]
        for (let i = alts.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1))
            ;[alts[i], alts[j]] = [alts[j], alts[i]]
        }
        return alts
    })

    const textParts = []
    let cursor = 0
    blanks.forEach((blank, idx) => {
        if (blank.startIndex > cursor) {
            textParts.push({ type: 'text', value: (text || '').slice(cursor, blank.startIndex) })
        }
        textParts.push({ type: 'blank', blankIndex: idx })
        cursor = blank.startIndex + blank.length
    })
    if (cursor < (text || '').length) {
        textParts.push({ type: 'text', value: text.slice(cursor) })
    }

    return { blanks, alternativesByBlank, textParts }
}

const useStyles = makeStyles((theme) => ({
    container: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        boxSizing: 'border-box',
    },
    scrollableArea: {
        flex: 1,
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
    },
    titleSection: {
        width: '100%',
        textAlign: 'center',
        padding: '12px 20px 8px',
    },
    titleText: {
        fontFamily: 'Rubik',
        fontSize: 18,
        fontWeight: 600,
        color: '#FFFFFF',
    },
    dialogueArea: {
        width: '100%',
        maxWidth: 480,
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        padding: '8px 16px',
        boxSizing: 'border-box',
        '@media (max-width: 599px)': {
            padding: '4px 8px',
            maxWidth: '100%',
            gap: 10,
        },
    },

    // ── Utterance rows ──
    utteranceRowLeft: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: 4,
        maxWidth: '88%',
        alignSelf: 'flex-start',
        '@media (max-width: 599px)': { maxWidth: '95%' },
    },
    utteranceRowRight: {
        display: 'flex',
        flexDirection: 'row-reverse',
        alignItems: 'flex-end',
        gap: 4,
        maxWidth: '88%',
        alignSelf: 'flex-end',
        '@media (max-width: 599px)': { maxWidth: '95%' },
    },
    utteranceRowMiddle: {
        alignSelf: 'center',
        maxWidth: '92%',
    },

    // ── Avatar ──
    avatarWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        flexShrink: 0,
    },
    avatarImg: {
        width: 44,
        height: 44,
    },
    audioIconButton: {
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        color: 'rgba(255, 255, 255, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'color 200ms ease',
        '&:hover': {
            color: '#FFFFFF',
        },
    },

    // ── Bubble ──
    bubble: {
        backgroundColor: '#FFFFFF',
        borderRadius: 16,
        padding: '12px 16px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        minWidth: 60,
    },
    bubbleTextLine: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 2,
        lineHeight: '180%',
    },
    bubbleWord: {
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 600,
        color: CapeeshColors.DarkText,
    },
    blankSlot: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 48,
        padding: '2px 8px',
        borderRadius: 6,
        backgroundColor: 'rgba(90, 180, 241, 0.2)',
        border: '1.5px solid rgba(90, 180, 241, 0.5)',
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 600,
        color: CapeeshColors.DarkText,
        minHeight: 24,
        transition: 'all 200ms ease',
    },
    blankSlotActive: {
        backgroundColor: 'rgba(90, 180, 241, 0.3)',
        borderColor: CapeeshColors.BlueBright,
        boxShadow: '0 0 0 2px rgba(90, 180, 241, 0.2)',
    },
    blankSlotFilled: {
        backgroundColor: 'rgba(90, 180, 241, 0.15)',
        borderColor: CapeeshColors.BlueBright,
    },
    blankSlotCorrect: {
        backgroundColor: 'rgba(128, 211, 135, 0.2)',
        borderColor: CapeeshColors.GreenBright,
    },
    blankSlotExam: {
        backgroundColor: 'rgba(156, 168, 184, 0.25)',
        borderColor: 'rgba(156, 168, 184, 0.6)',
    },
    translationText: {
        fontFamily: 'Rubik',
        fontSize: 13,
        fontWeight: 400,
        color: CapeeshColors.Gray1,
        marginTop: 4,
        lineHeight: '130%',
    },

    // ── Inline writing input (inside bubble) ──
    inlineInput: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 56,
        padding: '2px 8px',
        borderRadius: 6,
        border: 'none',
        borderBottom: `2px solid ${CapeeshColors.BlueBright}`,
        backgroundColor: 'rgba(90, 180, 241, 0.15)',
        boxShadow: '0 0 0 1px rgba(90, 180, 241, 0.3)',
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 600,
        color: CapeeshColors.DarkText,
        minHeight: 24,
        textAlign: 'center',
        outline: 'none',
        caretColor: CapeeshColors.DarkText,
        boxSizing: 'content-box',
        transition: 'border-color 200ms ease, box-shadow 200ms ease, background-color 200ms ease',
    },
    '@keyframes inlineInputShake': {
        '0%, 100%': { transform: 'translateX(0)' },
        '20%': { transform: 'translateX(-4px)' },
        '40%': { transform: 'translateX(4px)' },
        '60%': { transform: 'translateX(-3px)' },
        '80%': { transform: 'translateX(2px)' },
    },
    inlineInputWrong: {
        animation: '$inlineInputShake 400ms ease',
        borderBottom: `2px solid ${CapeeshColors.PinkBright}`,
        boxShadow: `0 0 0 1px ${CapeeshColors.PinkBright}`,
        backgroundColor: 'rgba(255, 102, 110, 0.12)',
    },

    // ── Narrator bar ──
    narratorBar: {
        backgroundColor: 'rgba(156, 168, 184, 0.4)',
        borderRadius: 10,
        padding: '8px 16px',
        alignSelf: 'center',
        maxWidth: '92%',
    },
    narratorText: {
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 400,
        color: '#FFFFFF',
        textAlign: 'center',
    },

    // ── Inline controls (below active bubble in dialogue flow) ──
    inlineControlsWrapper: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        padding: '4px 0',
        width: '100%',
    },
    chipsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
        justifyContent: 'center',
        width: '100%',
        '@media (max-width: 599px)': {
            gap: 6,
        },
    },
    chip: {
        padding: '10px 20px',
        borderRadius: 20,
        border: '2px solid rgba(255, 255, 255, 0.3)',
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 500,
        cursor: 'pointer',
        transition: 'all 200ms ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.16)',
            borderColor: 'rgba(255, 255, 255, 0.5)',
        },
        '@media (max-width: 599px)': {
            padding: '8px 14px',
            fontSize: 13,
        },
    },
    chipUsed: {
        opacity: 0.3,
        cursor: 'default',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            borderColor: 'rgba(255, 255, 255, 0.3)',
        },
    },
    '@keyframes chipShake': {
        '0%, 100%': { transform: 'translateX(0)' },
        '20%': { transform: 'translateX(-5px)' },
        '40%': { transform: 'translateX(5px)' },
        '60%': { transform: 'translateX(-3px)' },
        '80%': { transform: 'translateX(3px)' },
    },
    chipFlashWrong: {
        animation: '$chipShake 400ms ease',
        borderColor: `${CapeeshColors.PinkBright} !important`,
        backgroundColor: 'rgba(255, 102, 110, 0.2) !important',
        pointerEvents: 'none',
    },

    // ── Writing helpers (below bubble in dialogue flow) ──
    writingHelperArea: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        width: '100%',
    },
    checkButton: {
        padding: '10px 24px',
        borderRadius: 20,
        border: 'none',
        backgroundColor: CapeeshColors.BlueBright,
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 200ms ease',
        '&:disabled': {
            opacity: 0.4,
            cursor: 'default',
        },
    },
    helperKeyboard: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        justifyContent: 'center',
        width: '100%',
    },
    helperKey: {
        minWidth: 40,
        height: 40,
        padding: '0 6px',
        borderRadius: 8,
        border: '1px solid rgba(255, 255, 255, 0.25)',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 500,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 150ms ease',
        userSelect: 'none',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.22)',
        },
    },
    revealedHint: {
        fontFamily: 'Rubik',
        fontSize: 13,
        fontWeight: 500,
        color: 'rgba(255, 255, 255, 0.6)',
        textAlign: 'center',
    },
    revealedWord: {
        color: '#FFFFFF',
        fontWeight: 600,
    },

    // ── Multi-choice (below bubble in dialogue flow) ──
    multiChoiceContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        width: '100%',
    },
    multiChoiceOption: {
        padding: '12px 20px',
        borderRadius: 16,
        border: '2px solid rgba(255, 255, 255, 0.3)',
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        color: '#FFFFFF',
        fontFamily: 'Rubik',
        fontSize: 15,
        fontWeight: 500,
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all 200ms ease',
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.16)',
            borderColor: 'rgba(255, 255, 255, 0.5)',
        },
    },
    multiChoiceCorrect: {
        borderColor: CapeeshColors.GreenBright,
        backgroundColor: 'rgba(128, 211, 135, 0.2)',
    },
    multiChoiceIncorrect: {
        borderColor: CapeeshColors.PinkBright,
        backgroundColor: 'rgba(255, 102, 110, 0.2)',
    },
    multiChoiceExamSelected: {
        borderColor: 'rgba(255, 255, 255, 0.45)',
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
    },

    // ── Bottom section ──
    bottomSection: {
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px 20px',
        boxSizing: 'border-box',
        width: '100%',
        maxWidth: 480,
        margin: '0 auto',
        '@media (max-width: 599px)': {
            padding: '6px 8px 10px',
            maxWidth: 'none',
        },
    },
    infoBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        padding: '10px 16px',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        borderRadius: 12,
        boxSizing: 'border-box',
    },
    infoText: {
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 500,
        color: 'rgba(255, 255, 255, 0.8)',
    },
    progressText: {
        fontFamily: 'Rubik',
        fontSize: 14,
        fontWeight: 600,
        color: '#FFFFFF',
    },

    continueButton: {
        width: '100%',
        padding: '14px 48px',
        borderRadius: 28,
        border: 'none',
        backgroundColor: '#FFFFFF',
        color: '#1A1A2E',
        fontFamily: 'Rubik',
        fontSize: 16,
        fontWeight: 600,
        cursor: 'pointer',
        boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.15)',
        transition: 'transform 150ms ease, opacity 200ms ease',
        '&:hover': {
            transform: 'translateY(-1px)',
        },
        '&:disabled': {
            opacity: 0.4,
            cursor: 'default',
            transform: 'none',
        },
    },

    '@keyframes fadeInUp': {
        from: { opacity: 0, transform: 'translateY(12px)' },
        to: { opacity: 1, transform: 'translateY(0)' },
    },
    fadeInWrapper: {
        animation: '$fadeInUp 400ms ease forwards',
    },
}))

/**
 * TestDialogNode — renders a dialogue between mentors with sequential reveal,
 * speech bubbles, audio playback, and fill-in-the-blank / multi-choice interactions.
 *
 * Interaction controls render inline in the dialogue flow:
 *  - Distractor chips / sentence-builder chips appear directly below the active bubble
 *  - Writing-mode inputs render inside the bubble text itself
 *  - Completed answers persist in their bubbles after advancing
 */
const TestDialogNode = ({ node, imageMap, audioMap, onNodeComplete, isExamMode, l2LanguageId, isNativeSpeaker = false }) => {
    const classes = useStyles()
    const intl = useIntl()
    const audioPlayer = useAudioPlayer()
    const isTouchDevice = useIsTouchDevice()
    const dialogueEndRef = useRef(null)

    const testOptions = node.testOptions || []

    const scoredOptions = useMemo(
        () => testOptions.filter((to) => !NON_INTERACTIVE_TYPES.has(to.scenarioInteractiveElementType)),
        [testOptions]
    )
    const hasInteractions = scoredOptions.length > 0

    // ── Precompute blank data per testOption ──
    const blankDataByIndex = useMemo(() => {
        return testOptions.map((to) => {
            const isInteractive = !NON_INTERACTIVE_TYPES.has(to.scenarioInteractiveElementType)
            if (!isInteractive) return null
            const isFillInBlank =
                to.scenarioInteractiveElementType === InteractiveElementType.FillInBlankDistractors ||
                to.scenarioInteractiveElementType === InteractiveElementType.FillInBlankWriting
            if (!isFillInBlank) return null
            return parseBlanks(to.alternatives, to.text)
        })
    }, [testOptions])

    // ── Sequential reveal ──
    const [visibleCount, setVisibleCount] = useState(1)
    const allRevealed = visibleCount >= testOptions.length

    // ── Interaction completion tracking (across all utterances) ──
    const [completedInteractions, setCompletedInteractions] = useState({})
    const interactiveResultsRef = useRef([])

    // ── Display completion for non-interactive utterances ──
    const [displayCompleted, setDisplayCompleted] = useState({})

    // ── Persistent blank answers keyed by testOptionId ──
    // Stores final answers for each completed utterance so bubbles keep showing them.
    const [savedAnswers, setSavedAnswers] = useState({})

    // ── Per-utterance fill-in-blank state (active utterance only) ──
    const [blankAnswers, setBlankAnswers] = useState({})
    const [activeBlankIndex, setActiveBlankIndex] = useState(0)
    const [mistakesByBlank, setMistakesByBlank] = useState({})
    const [flashingChip, setFlashingChip] = useState(null)
    const flashTimerRef = useRef(null)

    // ── Writing mode state ──
    const [inputValue, setInputValue] = useState('')
    const [writingFailCount, setWritingFailCount] = useState(0)
    const [revealedAnswer, setRevealedAnswer] = useState(null)
    const [inputFlashing, setInputFlashing] = useState(false)
    const [inputFlashKey, setInputFlashKey] = useState(0)
    const inputFlashTimerRef = useRef(null)
    const inputRef = useRef(null)

    // ── Sentence builder state ──
    const [usedChipIds, setUsedChipIds] = useState(new Set())

    // ── Multi-choice state ──
    const [mcFlashing, setMcFlashing] = useState(null)
    const [mcHadMistake, setMcHadMistake] = useState(false)
    const [mcSelectedAnswer, setMcSelectedAnswer] = useState(null)

    const allScorableDone = scoredOptions.every((to) => completedInteractions[to.testOptionId])

    const currentIdx = visibleCount - 1
    const currentOption = testOptions[currentIdx]
    const currentIsInteractive = currentOption && !NON_INTERACTIVE_TYPES.has(currentOption.scenarioInteractiveElementType)

    // ── Sentence builder detection for the current active utterance ──
    // When all meaningful text is inside the alternatives (no text between blanks),
    // all chips from all blanks are pooled together and the user builds the sentence.
    const currentBlankData = blankDataByIndex[currentIdx] || null
    const isSentenceBuilder = useMemo(() => {
        if (!currentBlankData || currentBlankData.blanks.length <= 1) return false
        const meaningfulText = currentBlankData.textParts
            .filter((p) => p.type === 'text')
            .map((p) => p.value.trim())
            .join('')
        return meaningfulText.length === 0
    }, [currentBlankData])

    const pooledChips = useMemo(() => {
        if (!isSentenceBuilder || !currentBlankData) return []
        const chips = []
        currentBlankData.alternativesByBlank.forEach((alts, blankIdx) => {
            alts.forEach((alt, altIdx) => {
                chips.push({ ...alt, originalBlankIndex: blankIdx, chipId: `${blankIdx}-${altIdx}` })
            })
        })
        for (let i = chips.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1))
            ;[chips[i], chips[j]] = [chips[j], chips[i]]
        }
        return chips
    }, [isSentenceBuilder, currentBlankData])

    const resetInteractionState = useCallback(() => {
        setBlankAnswers({})
        setActiveBlankIndex(0)
        setMistakesByBlank({})
        setFlashingChip(null)
        setUsedChipIds(new Set())
        setInputValue('')
        setWritingFailCount(0)
        setRevealedAnswer(null)
        setInputFlashing(false)
        setMcFlashing(null)
        setMcHadMistake(false)
        setMcSelectedAnswer(null)
    }, [])

    // ── Audio on mount ──
    useEffect(() => {
        const bgUrl = node.backgroundAudioUrl ? audioMap[node.backgroundAudioUrl] : null
        if (bgUrl) audioPlayer.playAudio(bgUrl)
        return () => audioPlayer.stopAudio()
    }, [])

    // ── Auto-scroll to latest utterance ──
    useEffect(() => {
        if (dialogueEndRef.current) {
            dialogueEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
        }
    }, [visibleCount])

    // ── Handle non-interactive utterance: play audio, then mark display complete ──
    useEffect(() => {
        if (!currentOption) return
        if (currentIsInteractive) return
        if (displayCompleted[currentOption.testOptionId]) return

        let cancelled = false
        const run = async () => {
            const isL2 = l2LanguageId != null && currentOption.textLanguageId === l2LanguageId
            const audioPlayType = currentOption.audioPlayType ?? AudioPlayType.DontPlay
            const audioBlobUrl = currentOption.textAudioUrl && audioMap ? audioMap[currentOption.textAudioUrl] : null
            const shouldPlayAudio = isL2 && audioBlobUrl && audioPlayType !== AudioPlayType.DontPlay

            if (shouldPlayAudio && audioPlayType === AudioPlayType.PlayBefore) {
                await new Promise((r) => setTimeout(r, 300))
                if (cancelled) return
                await audioPlayer.playAudio(audioBlobUrl)
                if (cancelled) return
            }

            if (shouldPlayAudio && audioPlayType === AudioPlayType.PlayAfter) {
                await new Promise((r) => setTimeout(r, 400))
                if (cancelled) return
                await audioPlayer.playAudio(audioBlobUrl)
                if (cancelled) return
            }

            if (!cancelled) {
                setDisplayCompleted((prev) => ({
                    ...prev,
                    [currentOption.testOptionId]: { hadAudio: shouldPlayAudio },
                }))
            }
        }

        run()
        return () => { cancelled = true }
    }, [currentIdx, currentOption, currentIsInteractive, displayCompleted, audioMap, l2LanguageId])

    // ── Play audio for interactive utterance when it appears ──
    useEffect(() => {
        if (!currentOption || !currentIsInteractive) return
        if (completedInteractions[currentOption.testOptionId]) return

        const isL2 = l2LanguageId != null && currentOption.textLanguageId === l2LanguageId
        const audioPlayType = currentOption.audioPlayType ?? AudioPlayType.DontPlay
        const audioBlobUrl = currentOption.textAudioUrl && audioMap ? audioMap[currentOption.textAudioUrl] : null
        const shouldPlay = isL2 && audioBlobUrl && audioPlayType === AudioPlayType.PlayBefore

        if (!shouldPlay) return

        let cancelled = false
        const timer = setTimeout(() => {
            if (!cancelled) audioPlayer.playAudio(audioBlobUrl)
        }, 300)

        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [currentIdx, currentIsInteractive])

    // ── Sequential advancement ──
    useEffect(() => {
        if (visibleCount > testOptions.length) return
        if (!currentOption) return

        if (!currentIsInteractive && displayCompleted[currentOption.testOptionId]) {
            const meta = displayCompleted[currentOption.testOptionId]
            const baseDelay = calculateReadingDelay(currentOption.text)
            const delay = meta.hadAudio ? Math.min(baseDelay, 800) : baseDelay
            const timer = setTimeout(() => {
                resetInteractionState()
                setVisibleCount((prev) => Math.min(prev + 1, testOptions.length))
            }, delay)
            return () => clearTimeout(timer)
        }

        if (currentIsInteractive && completedInteractions[currentOption.testOptionId]) {
            const timer = setTimeout(() => {
                resetInteractionState()
                setVisibleCount((prev) => Math.min(prev + 1, testOptions.length))
            }, 600)
            return () => clearTimeout(timer)
        }
    }, [visibleCount, displayCompleted, completedInteractions, testOptions, currentIsInteractive, currentOption])

    // Cleanup timers
    useEffect(() => {
        return () => {
            if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
            if (inputFlashTimerRef.current) clearTimeout(inputFlashTimerRef.current)
        }
    }, [])

    // ── Finish an interaction: persist answers, score, advance ──
    const finishInteraction = useCallback(
        (testOption, finalAnswers, blankData) => {
            // Persist answers so the bubble keeps showing them
            setSavedAnswers((prev) => ({ ...prev, [testOption.testOptionId]: { ...finalAnswers } }))
            setCompletedInteractions((prev) => ({ ...prev, [testOption.testOptionId]: true }))

            const answers = (blankData?.blanks || []).map((_, idx) => ({
                testOptionId: testOption.testOptionId,
                answer: finalAnswers[idx]?.text || '',
                correct: finalAnswers[idx]?.correct || false,
                failCount: finalAnswers[idx]?.failCount || 0,
                wrongTryCount: finalAnswers[idx]?.wrongTryCount || 0,
            }))

            const evaluation = evaluateInteractive(testOption, answers, isExamMode)
            interactiveResultsRef.current.push({ ...evaluation, answers })

            const isL2 = l2LanguageId != null && testOption.textLanguageId === l2LanguageId
            const audioPlayType = testOption.audioPlayType ?? AudioPlayType.DontPlay
            const audioBlobUrl = testOption.textAudioUrl && audioMap ? audioMap[testOption.textAudioUrl] : null
            if (isL2 && audioBlobUrl && audioPlayType === AudioPlayType.PlayAfter) {
                audioPlayer.playAudio(audioBlobUrl)
            }
        },
        [audioMap, l2LanguageId, audioPlayer]
    )

    const advanceBlankOrSubmit = useCallback(
        (newAnswers, blankData) => {
            if (!blankData) return
            const nextUnfilled = blankData.blanks.findIndex((_, idx) => idx > activeBlankIndex && !newAnswers[idx])
            if (nextUnfilled >= 0) {
                setActiveBlankIndex(nextUnfilled)
            } else {
                finishInteraction(currentOption, newAnswers, blankData)
            }
        },
        [activeBlankIndex, currentOption, finishInteraction]
    )

    // ── Distractor chip tap ──
    const handleChipTap = useCallback(
        (alt, blankData) => {
            if (!currentOption || completedInteractions[currentOption.testOptionId] || flashingChip) return

            if (isExamMode) {
                const newAnswers = { ...blankAnswers, [activeBlankIndex]: { text: alt.text, correct: alt.correct } }
                setBlankAnswers(newAnswers)
                advanceBlankOrSubmit(newAnswers, blankData)
                return
            }

            if (!alt.correct) {
                setMistakesByBlank((prev) => ({
                    ...prev,
                    [activeBlankIndex]: (prev[activeBlankIndex] || 0) + 1,
                }))
                setFlashingChip(alt.text)
                if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
                flashTimerRef.current = setTimeout(() => setFlashingChip(null), 500)
                return
            }

            const wrongTryCount = mistakesByBlank[activeBlankIndex] || 0
            const newAnswers = {
                ...blankAnswers,
                [activeBlankIndex]: { text: alt.text, correct: wrongTryCount === 0, wrongTryCount },
            }
            setBlankAnswers(newAnswers)
            advanceBlankOrSubmit(newAnswers, blankData)
        },
        [currentOption, completedInteractions, flashingChip, isExamMode, blankAnswers, activeBlankIndex, mistakesByBlank, advanceBlankOrSubmit]
    )

    // ── Sentence builder: pooled chip tap ──
    const handlePooledChipTap = useCallback(
        (chip, blankData) => {
            if (!currentOption || completedInteractions[currentOption.testOptionId] || usedChipIds.has(chip.chipId) || flashingChip) return

            const correctForBlank = (blankData.alternativesByBlank[activeBlankIndex] || []).some(
                (a) => a.correct && a.text === chip.text
            )

            if (isExamMode) {
                const newAnswers = {
                    ...blankAnswers,
                    [activeBlankIndex]: { text: chip.text, correct: correctForBlank },
                }
                setBlankAnswers(newAnswers)
                setUsedChipIds((prev) => new Set([...prev, chip.chipId]))
                advanceBlankOrSubmit(newAnswers, blankData)
                return
            }

            if (!correctForBlank) {
                setMistakesByBlank((prev) => ({
                    ...prev,
                    [activeBlankIndex]: (prev[activeBlankIndex] || 0) + 1,
                }))
                setFlashingChip(chip.chipId)
                if (flashTimerRef.current) clearTimeout(flashTimerRef.current)
                flashTimerRef.current = setTimeout(() => setFlashingChip(null), 500)
                return
            }

            const wrongTryCount = mistakesByBlank[activeBlankIndex] || 0
            const newAnswers = {
                ...blankAnswers,
                [activeBlankIndex]: { text: chip.text, correct: wrongTryCount === 0, wrongTryCount },
            }
            setBlankAnswers(newAnswers)
            setUsedChipIds((prev) => new Set([...prev, chip.chipId]))
            advanceBlankOrSubmit(newAnswers, blankData)
        },
        [currentOption, completedInteractions, usedChipIds, flashingChip, isExamMode, blankAnswers, activeBlankIndex, mistakesByBlank, advanceBlankOrSubmit]
    )

    // ── Writing mode submit ──
    const handleWritingSubmit = useCallback(
        (blankData) => {
            if (!inputValue.trim() || inputFlashing || !blankData) return
            const normalized = inputValue.trim().toLowerCase()
            const blankAlts = blankData.alternativesByBlank[activeBlankIndex] || []
            const correctAlt = blankAlts.find((a) => a.correct)
            const isCorrect = blankAlts.some((a) => a.correct && a.text.trim().toLowerCase() === normalized)

            if (isExamMode) {
                const newAnswers = { ...blankAnswers, [activeBlankIndex]: { text: inputValue.trim(), correct: isCorrect } }
                setBlankAnswers(newAnswers)
                setInputValue('')
                advanceBlankOrSubmit(newAnswers, blankData)
                return
            }

            if (isCorrect) {
                const newAnswers = {
                    ...blankAnswers,
                    [activeBlankIndex]: {
                        text: inputValue.trim(),
                        correct: writingFailCount === 0,
                        failCount: writingFailCount,
                    },
                }
                setBlankAnswers(newAnswers)
                setInputValue('')
                advanceBlankOrSubmit(newAnswers, blankData)
                return
            }

            setInputFlashing(true)
            setInputFlashKey((k) => k + 1)
            if (inputFlashTimerRef.current) clearTimeout(inputFlashTimerRef.current)
            inputFlashTimerRef.current = setTimeout(() => setInputFlashing(false), 500)
            setMistakesByBlank((prev) => ({
                ...prev,
                [activeBlankIndex]: (prev[activeBlankIndex] || 0) + 1,
            }))

            const newFailCount = writingFailCount + 1
            setWritingFailCount(newFailCount)
            setInputValue('')

            if (newFailCount >= 2) {
                const blank = blankData.blanks[activeBlankIndex]
                const originalWord = blank && blank.startIndex >= 0 && currentOption?.text
                    ? currentOption.text.slice(blank.startIndex, blank.startIndex + blank.length)
                    : correctAlt?.text
                if (originalWord) setRevealedAnswer(originalWord)
            }
        },
        [inputValue, inputFlashing, blankAnswers, activeBlankIndex, isExamMode, writingFailCount, currentOption, advanceBlankOrSubmit]
    )

    // ── Multi-choice answer ──
    const handleMultiChoiceSelect = useCallback(
        (alt) => {
            if (!currentOption || completedInteractions[currentOption.testOptionId] || mcFlashing) return

            if (isExamMode || alt.correct) {
                const hadMistake = mcHadMistake
                setMcSelectedAnswer(alt.text)
                setSavedAnswers((prev) => ({ ...prev, [currentOption.testOptionId]: { mcAnswer: alt.text } }))
                setCompletedInteractions((prev) => ({ ...prev, [currentOption.testOptionId]: true }))

                const answers = [{
                    testOptionId: currentOption.testOptionId,
                    answer: alt.text,
                    correct: alt.correct && !hadMistake,
                }]
                const evaluation = evaluateInteractive(currentOption, answers, isExamMode)
                interactiveResultsRef.current.push({ ...evaluation, answers })
                return
            }

            setMcHadMistake(true)
            setMcFlashing(alt.text)
            setTimeout(() => setMcFlashing(null), 500)
        },
        [currentOption, completedInteractions, isExamMode, mcFlashing, mcHadMistake]
    )

    // ── Continue ──
    const handleContinue = useCallback(() => {
        audioPlayer.stopAudio()
        onNodeComplete(
            {
                nodeId: node.nodeId,
                chosenAlternativeIndex: 0,
            },
            interactiveResultsRef.current
        )
    }, [node.nodeId, onNodeComplete, audioPlayer])

    useEffect(() => {
        const canContinue = allScorableDone && allRevealed
        if (!canContinue) return
        const onKey = (e) => {
            if (e.key !== 'Enter') return
            const tag = document.activeElement?.tagName
            if (tag === 'INPUT' || tag === 'TEXTAREA') return
            handleContinue()
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [allScorableDone, allRevealed, handleContinue])

    // ── Play utterance audio manually ──
    const handlePlayAudio = useCallback(
        (testOption) => {
            const blobUrl = testOption.textAudioUrl && audioMap ? audioMap[testOption.textAudioUrl] : null
            if (blobUrl) audioPlayer.playAudio(blobUrl)
        },
        [audioMap, audioPlayer]
    )

    // ── Special characters for writing mode ──
    // Includes non-ASCII letters and non-letter characters (hyphens, apostrophes, etc.)
    const specialChars = useMemo(() => {
        if (!currentOption) return []
        const isWriting = currentOption.scenarioInteractiveElementType === InteractiveElementType.FillInBlankWriting
        if (!isWriting) return []
        const chars = new Set()
        ;(currentOption.alternatives || []).forEach((alt) => {
            if (!alt.text) return
            for (const ch of alt.text) {
                const lower = ch.toLowerCase()
                if (/[^\x00-\x7F]/.test(ch) && /\p{L}/u.test(ch)) {
                    chars.add(lower)
                } else if (!/\p{L}/u.test(ch) && ch !== ' ' && ch.trim()) {
                    chars.add(ch)
                }
            }
        })
        return Array.from(chars).sort()
    }, [currentOption])

    const handleSpecialCharInsert = useCallback(
        (char) => {
            const input = inputRef.current
            if (!input) return
            const start = input.selectionStart ?? inputValue.length
            const end = input.selectionEnd ?? inputValue.length
            const newValue = inputValue.slice(0, start) + char + inputValue.slice(end)
            setInputValue(newValue)
            requestAnimationFrame(() => {
                input.focus()
                input.selectionStart = input.selectionEnd = start + char.length
            })
        },
        [inputValue]
    )

    const handleKeyboardKeyPress = useCallback(
        (char) => {
            const input = inputRef.current
            if (!input) {
                setInputValue((prev) => prev + char)
                return
            }
            const start = input.selectionStart ?? inputValue.length
            const end = input.selectionEnd ?? inputValue.length
            const newValue = inputValue.slice(0, start) + char + inputValue.slice(end)
            setInputValue(newValue)
            requestAnimationFrame(() => {
                input.focus()
                input.selectionStart = input.selectionEnd = start + char.length
            })
        },
        [inputValue]
    )

    const handleKeyboardBackspace = useCallback(() => {
        const input = inputRef.current
        if (!input) {
            setInputValue((prev) => prev.slice(0, -1))
            return
        }
        const start = input.selectionStart ?? inputValue.length
        const end = input.selectionEnd ?? inputValue.length
        if (start !== end) {
            const newValue = inputValue.slice(0, start) + inputValue.slice(end)
            setInputValue(newValue)
            requestAnimationFrame(() => {
                input.focus()
                input.selectionStart = input.selectionEnd = start
            })
        } else if (start > 0) {
            const newValue = inputValue.slice(0, start - 1) + inputValue.slice(start)
            setInputValue(newValue)
            requestAnimationFrame(() => {
                input.focus()
                input.selectionStart = input.selectionEnd = start - 1
            })
        }
    }, [inputValue])

    const allAlternativeTexts = useMemo(() => {
        if (!currentOption) return []
        return (currentOption.alternatives || []).filter((a) => a.text).map((a) => a.text)
    }, [currentOption])

    // ── Titles from node.texts ──
    const titleTexts = (node.texts || []).filter((t) => t.textType === 1 || t.textType === 2)

    // ── Count completed interactive utterances ──
    const completedInteractiveCount = scoredOptions.filter((to) => completedInteractions[to.testOptionId]).length

    // ── Resolve answers for a given utterance (saved or live) ──
    const getAnswersForUtterance = useCallback(
        (testOption, idx) => {
            if (savedAnswers[testOption.testOptionId]) return savedAnswers[testOption.testOptionId]
            if (idx === currentIdx) return blankAnswers
            return {}
        },
        [savedAnswers, blankAnswers, currentIdx]
    )

    // ── Render bubble text with blanks (and inline writing input) ──
    const renderBubbleText = (testOption, idx, blankData) => {
        const isActive = idx === currentIdx && currentIsInteractive && !completedInteractions[testOption.testOptionId]
        const isCompleted = !NON_INTERACTIVE_TYPES.has(testOption.scenarioInteractiveElementType) && completedInteractions[testOption.testOptionId]
        const answers = getAnswersForUtterance(testOption, idx)
        const isWriting = testOption.scenarioInteractiveElementType === InteractiveElementType.FillInBlankWriting

        if (blankData && blankData.blanks.length > 0 && blankData.textParts.length > 0) {
            return (
                <div className={classes.bubbleTextLine}>
                    {blankData.textParts.map((part, pi) => {
                        if (part.type === 'text') {
                            return <span key={pi} className={classes.bubbleWord}>{renderFormattedText(part.value)}</span>
                        }

                        const bi = part.blankIndex
                        const answer = answers[bi]
                        const isActiveBlank = isActive && bi === activeBlankIndex

                        // Writing mode: render inline input for the active unfilled blank
                        if (isWriting && isActiveBlank && !answer) {
                            const inputClassName = inputFlashing
                                ? `${classes.inlineInput} ${classes.inlineInputWrong}`
                                : classes.inlineInput
                            return (
                                <input
                                    key={`input-${bi}-${inputFlashKey}`}
                                    ref={inputRef}
                                    className={inputClassName}
                                    type="text"
                                    inputMode={isTouchDevice ? 'none' : undefined}
                                    value={inputValue}
                                    onChange={(e) => setInputValue(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleWritingSubmit(blankData)}
                                    style={{ width: `${Math.max(5, inputValue.length + 2)}ch` }}
                                    autoFocus
                                />
                            )
                        }

                        let slotClass = classes.blankSlot
                        if (isCompleted && answer) {
                            slotClass += isExamMode
                                ? ` ${classes.blankSlotExam}`
                                : ` ${classes.blankSlotCorrect}`
                        } else if (answer) {
                            slotClass += ` ${classes.blankSlotFilled}`
                        } else if (isActiveBlank) {
                            slotClass += ` ${classes.blankSlotActive}`
                        }
                        return (
                            <span key={pi} className={slotClass}>
                                {answer?.text || '\u00A0\u00A0\u00A0'}
                            </span>
                        )
                    })}
                </div>
            )
        }

        return <Typography className={classes.bubbleWord}>{renderFormattedText(testOption.text)}</Typography>
    }

    // ── Render a dialogue bubble ──
    const renderBubble = (testOption, idx) => {
        const position = getDialoguePosition(testOptions, idx)
        const avatar = getMentorAvatar(testOption.mentor)
        const isNarrator = testOption.mentor === MentorEnum.NoMentor
        const blankData = blankDataByIndex[idx]
        const hasAudio = testOption.textAudioUrl && testOption.audioPlayType !== AudioPlayType.DontPlay
        const translation = testOption.explanation || ''

        if (isNarrator) {
            return (
                <div className={classes.narratorBar}>
                    <Typography className={classes.narratorText}>{renderFormattedText(testOption.text)}</Typography>
                </div>
            )
        }

        const rowClass = position === 'right' ? classes.utteranceRowRight : classes.utteranceRowLeft

        return (
            <div className={rowClass}>
                <div className={classes.avatarWrapper}>
                    {avatar && <img src={avatar} alt="" className={classes.avatarImg} />}
                    {hasAudio && (
                        <button
                            className={classes.audioIconButton}
                            onClick={() => handlePlayAudio(testOption)}
                            aria-label={intl.formatMessage(playerMessages.playAudio)}
                        >
                            <VolumeUpIcon style={{ fontSize: 18 }} />
                        </button>
                    )}
                </div>
                <div className={classes.bubble}>
                    {renderBubbleText(testOption, idx, blankData)}
                    {!isNativeSpeaker && translation && (
                        <Typography className={classes.translationText}>{renderFormattedText(translation)}</Typography>
                    )}
                </div>
            </div>
        )
    }

    // ── Render inline interaction controls (below the active bubble) ──
    const renderInlineControls = (testOption, idx) => {
        const isActive = idx === currentIdx && currentIsInteractive && !completedInteractions[testOption.testOptionId]
        if (!isActive) return null

        const type = testOption.scenarioInteractiveElementType
        const blankData = blankDataByIndex[idx]

        // Distractor chips / sentence builder
        if (type === InteractiveElementType.FillInBlankDistractors && blankData) {
            return (
                <div className={classes.inlineControlsWrapper}>
                    <div className={classes.chipsContainer}>
                        {isSentenceBuilder
                            ? pooledChips.map((chip) => {
                                  const isUsed = usedChipIds.has(chip.chipId)
                                  const isFlashing = flashingChip === chip.chipId
                                  let chipClass = classes.chip
                                  if (isFlashing) chipClass += ` ${classes.chipFlashWrong}`
                                  else if (isUsed) chipClass += ` ${classes.chipUsed}`
                                  return (
                                      <button
                                          key={chip.chipId}
                                          className={chipClass}
                                          onClick={() => handlePooledChipTap(chip, blankData)}
                                          disabled={isUsed && !isFlashing}
                                      >
                                          {chip.text}
                                      </button>
                                  )
                              })
                            : (blankData.alternativesByBlank[activeBlankIndex] || []).map((alt, i) => {
                                  const isUsed = Object.values(blankAnswers).some((a) => a.text === alt.text)
                                  const isFlashing = flashingChip === alt.text
                                  let chipClass = classes.chip
                                  if (isFlashing) chipClass += ` ${classes.chipFlashWrong}`
                                  else if (isUsed) chipClass += ` ${classes.chipUsed}`
                                  return (
                                      <button
                                          key={i}
                                          className={chipClass}
                                          onClick={() => handleChipTap(alt, blankData)}
                                          disabled={isUsed && !isFlashing}
                                      >
                                          {alt.text}
                                      </button>
                                  )
                              })}
                    </div>
                </div>
            )
        }

        // Writing mode: helper keyboard, revealed hint, check button (input is inline in the bubble)
        if (type === InteractiveElementType.FillInBlankWriting && blankData) {
            return (
                <div className={classes.writingHelperArea}>
                    {revealedAnswer && (
                        <Typography className={classes.revealedHint}>
                            Type: <span className={classes.revealedWord}>{revealedAnswer}</span>
                        </Typography>
                    )}
                    {isTouchDevice ? (
                        <L2OnScreenKeyboard
                            languageId={l2LanguageId}
                            correctAnswers={allAlternativeTexts}
                            onKeyPress={handleKeyboardKeyPress}
                            onBackspace={handleKeyboardBackspace}
                            onSubmit={() => handleWritingSubmit(blankData)}
                            inputValue={inputValue}
                            submitLabel={isExamMode ? intl.formatMessage(playerMessages.submit) : intl.formatMessage(playerMessages.check)}
                            submitDisabled={inputFlashing}
                            isExamMode={isExamMode}
                        />
                    ) : (
                        <>
                            {specialChars.length > 0 && (
                                <div className={classes.helperKeyboard}>
                                    {specialChars.map((ch) => (
                                        <button
                                            key={ch}
                                            className={classes.helperKey}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => handleSpecialCharInsert(ch)}
                                        >
                                            {ch}
                                        </button>
                                    ))}
                                </div>
                            )}
                            <button
                                className={classes.checkButton}
                                onClick={() => handleWritingSubmit(blankData)}
                                disabled={!inputValue.trim() || inputFlashing}
                            >
                                <FormattedMessage {...playerMessages.check} />
                            </button>
                        </>
                    )}
                </div>
            )
        }

        // Multi-choice
        if (type === InteractiveElementType.MultiChoice) {
            const alternatives = testOption.alternatives || []
            return (
                <div className={classes.inlineControlsWrapper}>
                    <div className={classes.multiChoiceContainer}>
                        {alternatives.map((alt, i) => {
                            const isFlashing = mcFlashing === alt.text
                            let optClass = classes.multiChoiceOption
                            if (isFlashing) optClass += ` ${classes.multiChoiceIncorrect}`
                            return (
                                <button
                                    key={i}
                                    className={optClass}
                                    onClick={() => handleMultiChoiceSelect(alt)}
                                    disabled={isFlashing}
                                >
                                    {alt.text}
                                </button>
                            )
                        })}
                    </div>
                </div>
            )
        }

        return null
    }

    const visibleOptions = testOptions.slice(0, visibleCount)

    const infoLabel = hasInteractions
        ? intl.formatMessage(playerMessages.fillInBlanks)
        : intl.formatMessage(playerMessages.readContentContinue)

    const progressLabel = hasInteractions
        ? `${completedInteractiveCount} / ${scoredOptions.length}`
        : `${Math.min(visibleCount, testOptions.length)} / ${testOptions.length}`

    return (
        <div className={classes.container}>
            <div
                className={classes.scrollableArea}
                style={isTouchDevice ? { paddingBottom: 300 } : undefined}
            >
                {/* Title */}
                {titleTexts.length > 0 && (
                    <div className={classes.titleSection}>
                        {titleTexts.map((t, i) => (
                            <Typography key={i} className={classes.titleText}>{renderFormattedText(t.text)}</Typography>
                        ))}
                    </div>
                )}

                {/* Dialogue area — bubbles + inline interaction controls */}
                <div className={classes.dialogueArea}>
                    {visibleOptions.map((to, idx) => (
                        <React.Fragment key={to.testOptionId || idx}>
                            <div className={classes.fadeInWrapper}>
                                {renderBubble(to, idx)}
                            </div>
                            {renderInlineControls(to, idx)}
                        </React.Fragment>
                    ))}
                    <div ref={dialogueEndRef} />
                </div>
            </div>

            {/* Bottom section — info bar + continue only */}
            <div className={classes.bottomSection}>
                <div className={classes.infoBar}>
                    <Typography className={classes.infoText}>{infoLabel}</Typography>
                    <Typography className={classes.progressText}>{progressLabel}</Typography>
                </div>

                <button
                    className={classes.continueButton}
                    onClick={handleContinue}
                    disabled={!allScorableDone || !allRevealed}
                >
                    <FormattedMessage {...playerMessages.continueButton} />
                </button>
            </div>
        </div>
    )
}

export default TestDialogNode
